import { KitButton } from './button';
import { useEffect, useRef } from 'react';
import type { DataTableProps, TableRow, TableColumn } from './table-types';
import { TableEditor } from './table-editor';

export function TableCheck({
  checked,
  mixed = false,
  label,
  onChange,
}: {
  checked: boolean;
  mixed?: boolean;
  label: string;
  onChange(value: boolean): void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = mixed;
  }, [mixed]);
  return (
    <label className="pc-check">
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        aria-label={label}
        aria-checked={mixed ? 'mixed' : checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
      <span className="pc-check-box" aria-hidden="true" />
    </label>
  );
}

export function TableValue({
  row,
  column,
  props,
}: {
  row: TableRow;
  column: TableColumn;
  props: DataTableProps;
}) {
  const value = row.values[column.id];
  if (value === null || value === undefined) return <span aria-label={props.copy.noValue}>—</span>;
  const render = column.rendererId && props.renderers?.[column.rendererId];
  return <>{render ? render(value, row) : props.formatCell(value, column)}</>;
}

export function TableDesktopRow({
  row,
  index,
  props,
  selected,
  toggle,
}: {
  row: TableRow;
  index: number;
  props: DataTableProps;
  selected: boolean;
  toggle(value: boolean): void;
}) {
  const canSelect = props.onSelectionChange && (props.canSelect?.(row) ?? true);
  const editingRow = props.edit?.rowId === row.id;
  return (
    <div
      className="pc-dt-row"
      role="row"
      aria-rowindex={index + 2}
      aria-selected={selected}
      data-state={
        editingRow && props.edit?.state === 'saving'
          ? 'saving'
          : editingRow && props.edit?.state === 'invalid'
            ? 'error'
            : undefined
      }
      style={{
        gridTemplateColumns: `${props.onSelectionChange ? 'max-content ' : ''}repeat(${props.columns.length}, minmax(0, 1fr))`,
      }}
    >
      {props.onSelectionChange && (
        <div className="pc-dt-select" role="gridcell">
          {canSelect && (
            <TableCheck checked={selected} label={props.copy.selectRow(row)} onChange={toggle} />
          )}
        </div>
      )}
      {props.columns.map((column) => {
        const editable = props.onEdit && props.canEdit?.(row, column);
        const editing = editingRow && props.edit?.columnId === column.id;
        return (
          <div
            key={column.id}
            className="pc-dt-cell"
            role="gridcell"
            data-state={editing ? props.edit?.state : undefined}
            data-align={
              column.type === 'text' || column.type === 'bool' || column.type === 'status'
                ? 'start'
                : 'end'
            }
            data-type={column.type}
            tabIndex={editable ? 0 : undefined}
            onDoubleClick={() => {
              if (editable && !editing) props.onEdit?.(row.id, column.id);
            }}
            onKeyDown={(event) => {
              if (editable && !editing && event.key === 'Enter') {
                event.preventDefault();
                props.onEdit?.(row.id, column.id);
              }
            }}
          >
            {editing ? (
              <TableEditor props={props} row={row} column={column} />
            ) : (
              <TableValue row={row} column={column} props={props} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function TablePhoneRow({
  row,
  props,
  selected,
  toggle,
}: {
  row: TableRow;
  props: DataTableProps;
  selected: boolean;
  toggle(value: boolean): void;
}) {
  const primary =
    props.columns.find((column) => column.primary) ??
    props.columns.find((column) => column.type === 'text') ??
    props.columns[0];
  const pairs = props.columns.filter((column) => column !== primary).slice(0, 3);
  return (
    <div className="pc-dt-card" aria-selected={selected}>
      {props.selectMode && props.onSelectionChange && (props.canSelect?.(row) ?? true) && (
        <TableCheck checked={selected} label={props.copy.selectRow(row)} onChange={toggle} />
      )}
      {primary && (
        <div>
          <TableValue row={row} column={primary} props={props} />
        </div>
      )}
      {pairs.map((column) => (
        <div key={column.id} className="pc-dt-pair">
          <span>{column.label}</span>
          <TableValue row={row} column={column} props={props} />
        </div>
      ))}
      {props.onOpenRow && (
        <KitButton variant="quiet" type="button" onClick={() => props.onOpenRow?.(row.id)}>
          {props.copy.more(row)}
        </KitButton>
      )}
    </div>
  );
}
